import React from 'react';
import { FiShield, FiGlobe, FiCheckCircle, FiClock, FiLock, FiUsers, FiZap, FiBarChart2 } from 'react-icons/fi';

const SecureCloudERP = () => {
  const securityFeatures = [
    {
      icon: <FiShield className="w-6 h-6" />,
      title: 'End-to-End Encryption',
      description: 'All data is encrypted in transit and at rest. This means your student records, staff information, financial data, and other sensitive information are protected. Even if someone intercepts the data, they cannot read it without the encryption keys.',
      stat: '256-bit AES encryption standard protects all data',
      color: 'bg-indigo-50'
    },
    {
      icon: <FiLock className="w-6 h-6" />,
      title: 'Role-Based Access Control',
      description: 'Different users need different levels of access. Role-based access control ensures that teachers see only what they need, administrators see what they need, and parents see what they need. No unnecessary exposure of sensitive data.',
      stat: '95% of data breaches involve unnecessary data access',
      color: 'bg-emerald-50'
    },
    {
      icon: <FiUsers className="w-6 h-6" />,
      title: 'Multi-Factor Authentication',
      description: 'Passwords alone are not enough. Multi-factor authentication adds an extra layer of security. Users need something they know (password) and something they have (phone or authenticator app) to access the system.',
      stat: 'MFA blocks 99.9% of account compromise attacks',
      color: 'bg-amber-50'
    },
    {
      icon: <FiClock className="w-6 h-6" />,
      title: 'Real-Time Security Monitoring',
      description: 'Cloud providers continuously monitor for suspicious activity, unusual access patterns, and potential threats. If something looks wrong, it\'s flagged and addressed immediately.',
      stat: 'Cloud providers detect threats 24/7 with automated monitoring',
      color: 'bg-rose-50'
    },
    {
      icon: <FiBarChart2 className="w-6 h-6" />,
      title: 'Audit Trails and Compliance',
      description: 'Every action in the system is logged—who accessed what, when, and from where. This audit trail is essential for compliance with data protection regulations and for investigating any incidents.',
      stat: 'Complete audit logs for all system activity',
      color: 'bg-purple-50'
    },
    {
      icon: <FiGlobe className="w-6 h-6" />,
      title: 'Automatic Security Updates',
      description: 'Security threats evolve constantly. Cloud providers update security measures automatically—patching vulnerabilities, updating firewalls, and applying security fixes without any action required from you.',
      stat: 'Security patches applied within hours of discovery',
      color: 'bg-blue-50'
    }
  ];

  const accessibilityFeatures = [
    {
      title: 'Anywhere Access',
      description: 'Access from any device, any location, anytime. No VPNs, no remote desktop, no complex setup.',
      icon: <FiGlobe className="w-5 h-5" />
    },
    {
      title: 'Mobile-Friendly Interface',
      description: 'Designed for phones and tablets. Complete functionality on any screen size.',
      icon: <FiZap className="w-5 h-5" />
    },
    {
      title: '24/7 Availability',
      description: 'Accessible day and night. Teachers can check attendance late at night. Parents can view progress reports on weekends.',
      icon: <FiClock className="w-5 h-5" />
    },
    {
      title: 'Internet Resilience',
      description: 'Works on low bandwidth. Optimized for Indian internet conditions.',
      icon: <FiShield className="w-5 h-5" />
    },
    {
      title: 'Cross-Platform Support',
      description: 'Works on Windows, Mac, Android, iOS—any device you have.',
      icon: <FiUsers className="w-5 h-5" />
    },
    {
      title: 'Offline Capabilities',
      description: 'Some features work offline and sync when connectivity is restored.',
      icon: <FiCheckCircle className="w-5 h-5" />
    }
  ];

  const myths = [
    {
      title: 'Myth: Cloud is less secure than on-premise',
      reality: 'Cloud providers have more security expertise, resources, and infrastructure than most institutions can afford. Major cloud providers invest billions in security—more than any institution can.',
      icon: <FiShield className="w-5 h-5" />
    },
    {
      title: 'Myth: Accessibility means vulnerability',
      reality: 'Accessibility doesn\'t mean vulnerability. Modern security measures—encryption, MFA, role-based access—ensure that only authorized users access what they should.',
      icon: <FiLock className="w-5 h-5" />
    },
    {
      title: 'Myth: On-premise gives you more control',
      reality: 'Cloud gives you control without the burden. You control who accesses what. You control permissions. You control data. The cloud provider handles the infrastructure.',
      icon: <FiGlobe className="w-5 h-5" />
    }
  ];

  return (
    <>
      {/* Introduction */}
      <p className="text-lg text-slate-600 leading-relaxed mb-6">
        Educational leaders face a dilemma. They want teachers, parents, and administrators to access their ERP system from anywhere—from home, from their phones, from different campuses. But they also worry about security. Can a system that's accessible from anywhere really be secure?
      </p>
      
      <p className="text-lg text-slate-600 leading-relaxed mb-6">
        <strong className="text-slate-900">The answer is yes.</strong> Modern cloud ERPs are both secure and accessible. In fact, they often offer better security than on-premise systems that are locked behind campus firewalls.
      </p>

      <p className="text-lg text-slate-600 leading-relaxed mb-8">
        Here's how cloud ERP achieves both security and accessibility—without compromise.
      </p>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        <div className="bg-white rounded-xl p-6 text-center border">
          <div className="text-3xl font-extrabold text-indigo-600">256-bit</div>
          <div className="text-sm text-slate-500">AES encryption standard</div>
        </div>
        <div className="bg-white rounded-xl p-6 text-center border">
          <div className="text-3xl font-extrabold text-emerald-600">99.9%</div>
          <div className="text-sm text-slate-500">MFA blocks account compromise</div>
        </div>
        <div className="bg-white rounded-xl p-6 text-center border">
          <div className="text-3xl font-extrabold text-amber-600">24/7</div>
          <div className="text-sm text-slate-500">Security monitoring and updates</div>
        </div>
      </div>

      <h2 className="text-2xl font-bold text-slate-900 mb-6">How Cloud ERP Keeps Your Data Secure</h2>

      {/* Security Features */}
      {securityFeatures.map((item, i) => (
        <div key={i} className={`${item.color} rounded-2xl p-6 mb-6 border`}>
          <div className="flex items-start gap-4">
            <div className="bg-white rounded-xl p-3 shadow-sm flex-shrink-0 mt-1">
              {item.icon}
            </div>
            <div className="flex-1">
              <h3 className="text-xl font-bold text-slate-900 mb-2">{item.title}</h3>
              <p className="text-slate-600 leading-relaxed text-sm mb-4">{item.description}</p>
              <div className="bg-white/60 rounded-xl p-3 border border-amber-200">
                <p className="text-sm font-semibold text-amber-600">🔒 {item.stat}</p>
              </div>
            </div>
          </div>
        </div>
      ))}

      {/* Accessibility Features */}
      <div className="mt-8 bg-white rounded-2xl border border-slate-200 p-6 sm:p-8">
        <h3 className="text-xl font-bold text-slate-900 mb-4">Complete Accessibility—Without Compromise</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {accessibilityFeatures.map((item, i) => (
            <div key={i} className="bg-indigo-50 rounded-xl p-4 border border-indigo-200">
              <div className="flex items-start gap-3">
                <div className="bg-white rounded-lg p-2 flex-shrink-0">
                  {item.icon}
                </div>
                <div>
                  <h4 className="font-bold text-slate-900 text-sm">{item.title}</h4>
                  <p className="text-xs text-slate-600">{item.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Myths vs Reality */}
      <div className="mt-8 bg-white rounded-2xl border border-slate-200 p-6 sm:p-8">
        <h3 className="text-xl font-bold text-slate-900 mb-4">Myths About Cloud Security—Busted</h3>
        <div className="space-y-4">
          {myths.map((item, i) => (
            <div key={i} className="bg-amber-50 rounded-xl p-4 border border-amber-200">
              <div className="flex items-start gap-3">
                <div className="bg-white rounded-lg p-2 flex-shrink-0">
                  {item.icon}
                </div>
                <div>
                  <h4 className="font-bold text-rose-600 text-sm">{item.title}</h4>
                  <p className="text-sm text-slate-700 mt-1">{item.reality}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* The Balance */}
      <div className="mt-8 bg-white rounded-2xl border border-slate-200 p-6">
        <h3 className="text-lg font-bold text-slate-900 mb-3">The Balance: Security + Accessibility</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-200">
            <div className="text-center">
              <div className="text-2xl mb-1">🔒</div>
              <h4 className="font-bold text-slate-900 text-sm">Security You Can Trust</h4>
              <ul className="text-xs text-slate-600 mt-2 space-y-1 text-left">
                <li className="flex items-start gap-2">• <span>End-to-end encryption</span></li>
                <li className="flex items-start gap-2">• <span>Multi-factor authentication</span></li>
                <li className="flex items-start gap-2">• <span>Role-based access control</span></li>
                <li className="flex items-start gap-2">• <span>24/7 security monitoring</span></li>
              </ul>
            </div>
          </div>
          <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
            <div className="text-center">
              <div className="text-2xl mb-1">🌐</div>
              <h4 className="font-bold text-slate-900 text-sm">Accessibility You Need</h4>
              <ul className="text-xs text-slate-600 mt-2 space-y-1 text-left">
                <li className="flex items-start gap-2">• <span>Access from anywhere</span></li>
                <li className="flex items-start gap-2">• <span>Mobile-friendly interface</span></li>
                <li className="flex items-start gap-2">• <span>24/7 availability</span></li>
                <li className="flex items-start gap-2">• <span>Cross-platform support</span></li>
              </ul>
            </div>
          </div>
        </div>
        <p className="text-center text-sm text-slate-600 mt-4">
          <strong>Both are possible. Both are essential.</strong>
        </p>
      </div>

      {/* Why Truyon Section */}
      <div className="mt-8 bg-indigo-50 rounded-2xl p-6 border border-indigo-200">
        <h3 className="text-lg font-bold text-indigo-900 mb-3">Why Truyon Delivers Both Security and Accessibility</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">🔒</div>
            <h4 className="font-bold text-slate-900 text-sm">Enterprise Security</h4>
            <p className="text-xs text-slate-600">Bank-grade encryption and protection</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">🌐</div>
            <h4 className="font-bold text-slate-900 text-sm">Global Accessibility</h4>
            <p className="text-xs text-slate-600">Access from anywhere, any device</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">📱</div>
            <h4 className="font-bold text-slate-900 text-sm">Mobile-First Design</h4>
            <p className="text-xs text-slate-600">Full functionality on phones</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">🛡️</div>
            <h4 className="font-bold text-slate-900 text-sm">Role-Based Access</h4>
            <p className="text-xs text-slate-600">Granular access for every user</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">⚡</div>
            <h4 className="font-bold text-slate-900 text-sm">Auto Updates</h4>
            <p className="text-xs text-slate-600">Continuous security improvements</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">📊</div>
            <h4 className="font-bold text-slate-900 text-sm">Audit Trails</h4>
            <p className="text-xs text-slate-600">Complete visibility into system access</p>
          </div>
        </div>
      </div>

      {/* Conclusion */}
      <div className="mt-8 bg-indigo-50 rounded-2xl p-6 border border-indigo-200">
        <h3 className="text-lg font-bold text-indigo-900 mb-2">Secure. Accessible. No Compromise.</h3>
        <p className="text-indigo-800 leading-relaxed">
          Educational institutions don't have to choose between security and accessibility. Cloud ERP provides both. Stronger security than most on-premise systems can achieve. Complete accessibility from anywhere, any device.
        </p>
        <p className="text-indigo-800 leading-relaxed mt-3">
          Truyon delivers enterprise-grade security with end-to-end encryption, multi-factor authentication, role-based access, and 24/7 monitoring. And it's accessible from anywhere—designed for mobile, optimized for Indian networks.
        </p>
        <p className="text-indigo-800 leading-relaxed mt-3">
          <strong>Truyon for secure cloud ERP.</strong> Secure. Accessible. No compromise.
        </p>
      </div>
    </>
  );
};

export default SecureCloudERP;